// The JSP risk verdict. Pure: the job in, the verdict out. Reads nothing, writes nothing.
// Source: window.SafeTagVerdict in MOCKUP_JSP_APPROVED.html (rev 8, approved by Robert 2026-10-08).
// Decisions: RISK_ENGINE_DECISIONS.md 1 to 51 and RISK_ENGINE_DECISIONS_52_PLUS.md 52 to 61.
// Test cases: BENCH_WORDS.md (groups A to E), tests/unit/risk-verdict.test.ts.
//
// Rules carried in this file:
// * Field Assessment answers replace the enclosure picker (decision 52).
//   Covers No scores as "deadfront missing". Doors No scores as "open or damaged".
//   Damage, burn marks or discoloration scores as the old burn marks question.
// * Risk is final before the task questions. Task questions never raise the verdict (60, 61).
// * No device is named for isolation. The point names the kind of point only (55).
//   The QEW's lock and tag go on every isolation point (56).
// * The LDL rehearsal follows the voltage: three-phase 8 steps, single-phase 5 steps (57).
// * Voltage blank is never read as 0 V. IE blank reads IE pending study (39).
// * First operation after work raises the arc row on Operate tasks (64). The one task question that raises:
//   the QEW answers it at Job, before Risk, and cannot find it out without opening the equipment.

export type Level = "NONE" | "PENDING" | "LOW" | "MEDIUM" | "HIGH" | "DANGER";
export type RaiseLevel = "MEDIUM" | "HIGH" | "DANGER";
export type Serviceability = "Serviceable" | "Not Verified" | "Limited Service" | "Nonserviceable";
/** The engine reads Operate-open even though no active task carries it today (decisions 31, 34). */
export type VerdictTaskType = "Observe" | "Operate-open" | "Operate-close" | "Expose";
export type Tier = 1 | 2 | 3;

/** Upstream protection from the site walk. */
export type Upstream =
  | { state: "walkedClear" }
  | { state: "walkedOverduty"; deviceName: string }
  | { state: "notWalked" };

/** Equipment Field Assessment answers (decision 52). */
export interface FieldAssessment {
  /** Smell, Noise, Heat, Arcing. Empty when the answer was No. */
  sensesNoticed: string[];
  doorsClosedAndLatched: boolean;
  coversAndDeadfrontInPlace: boolean;
  damageBurnMarksOrDiscoloration: boolean;
  dustMoistureOrDebris: boolean;
  tamperSticker: "intact" | "broken" | "none";
  /** Expose only. Ignored on other task types. */
  handsWithin12In: boolean;
  /** Expose only. Ignored on other task types. */
  lineSideExposed: boolean;
}

export interface QewRaise {
  hazard: "shock" | "arc" | "both";
  level: RaiseLevel;
  reason: string;
}

export interface VerdictInput {
  /** From the asset record. null when blank. */
  voltage: number | null;
  /** cal/cm². null (blank) and "pending" both read IE pending study. */
  incidentEnergyCal: number | "pending" | "outsideStudy" | null;
  serviceability: Serviceability;
  overdutyThisDevice: boolean;
  upstream: Upstream;
  /** Program Settings. Alltech is 12. */
  topStockedPpeCal: number;
  /** Approved EEWP for this asset and task. Only read when IE is above 40. */
  approvedEewp: boolean;
  /** From the task copy. */
  tier: Tier;
  taskType: VerdictTaskType;
  /** Tier 2 Need Power? answer. null on Tier 1 and Tier 3. */
  needPower: "deEnergize" | "needsPower" | null;
  fieldAssessment: FieldAssessment;
  /** Decision 64: someone worked on this equipment since it last ran, or the QEW doesn't know. */
  firstOperationAfterWork?: boolean;
  qewRaise: QewRaise | null;
}

export interface ActionLine {
  text: string;
  /** "unknown" draws in the pending blue, "raisedByYou" in amber. */
  kind: "" | "unknown" | "raisedByYou";
}

export type FollowUp = "Work request" | "Engineering correction" | "Engineering review";

export interface IsolationPoint {
  kind: string;
  note: string;
}

export interface Verdict {
  shockLevel: Level;
  arcLevel: Level;
  topLevel: Level;
  headline: string;
  actionLines: ActionLine[];
  /** How It Got There, in order. */
  causes: string[];
  followUp: FollowUp[];
  isolationPoint: IsolationPoint;
  riskButton: string;
  /** DANGER or PENDING forces de-energized work. */
  forcedDeEnergize: boolean;
  deEnergized: boolean;
  rail: string[];
}

export const LEVEL_RANK: Record<Level, number> = { NONE: 0, PENDING: 0, LOW: 1, MEDIUM: 2, HIGH: 3, DANGER: 4 };

export const LEVEL_HEADLINE: Record<Level, string> = {
  NONE: "No shock or arc flash hazard under 50 V.",
  LOW: "Normal controls. Stay outside boundaries.",
  MEDIUM: "Required PPE. Set boundaries. Follow the JSP.",
  HIGH: "Full controls. Slow down. No shortcuts.",
  DANGER: "No energized work. De-energize. LOTO, verify, LDL.",
  PENDING: "Voltage not on record. No energized work. ESWC is still open.",
};

const UNKNOWN_IE = "Incident energy unknown. It could be far above your PPE.";
const LOCK_LINE = "Survey it in the field and check the one-line. Your lock and tag go on every isolation point.";

/** How the old enclosure answer reads from the Field Assessment (decision 52). */
type Enclosure = "secured" | "openOrDamaged" | "deadfrontMissing";
function enclosureOf(f: FieldAssessment): Enclosure {
  if (!f.coversAndDeadfrontInPlace) return "deadfrontMissing";
  if (!f.doorsClosedAndLatched) return "openOrDamaged";
  return "secured";
}

export function verdict(input: VerdictInput): Verdict {
  const causes: string[] = [];
  const actionLines: ActionLine[] = [];
  const followUp: FollowUp[] = [];
  const line = (text: string, kind: ActionLine["kind"] = "") => actionLines.push({ text, kind });
  const follow = (f: FollowUp) => {
    if (!followUp.includes(f)) followUp.push(f);
  };

  const v = input.voltage;
  const t = input.taskType;
  const observe = t === "Observe";
  const operate = t === "Operate-open" || t === "Operate-close";
  const expose = t === "Expose";
  const fa = input.fieldAssessment;
  const enclosure = enclosureOf(fa);
  const near = expose && fa.handsWithin12In;
  const lineSide = expose && fa.lineSideExposed;
  const sensed = fa.sensesNoticed.length > 0;
  const sensesText = fa.sensesNoticed.join(", ");

  const pending = input.incidentEnergyCal === null || input.incidentEnergyCal === "pending";
  const outsideStudy = input.incidentEnergyCal === "outsideStudy";
  const ie = typeof input.incidentEnergyCal === "number" ? input.incidentEnergyCal : null;
  const over40 = ie !== null && ie > 40;
  const overCeiling = ie !== null && ie >= input.topStockedPpeCal;
  const noEewp = over40 && !input.approvedEewp && !overCeiling;
  const withEewp = over40 && input.approvedEewp && !overCeiling;

  const svc = input.serviceability;
  const upstreamOverduty = input.upstream.state === "walkedOverduty";
  const anyOverduty = input.overdutyThisDevice || upstreamOverduty;
  const overdutyDevice = input.overdutyThisDevice
    ? "this device"
    : input.upstream.state === "walkedOverduty"
      ? input.upstream.deviceName
      : "";
  const notWalked = input.upstream.state === "notWalked";

  function raiseTo(current: Level, to: Level, cause: string, label: string): Level {
    if (current === "NONE" || current === "PENDING") return current;
    if (LEVEL_RANK[to] > LEVEL_RANK[current]) {
      causes.push(cause);
      return to;
    }
    causes.push(`${label}: recorded, covered by current controls.`);
    return current;
  }

  // Shock
  let shock: Level;
  if (v === null) {
    shock = "PENDING";
    causes.push("Shock PENDING: voltage not on record. Never read as 0 V.");
  } else if (v < 50) {
    shock = "NONE";
    causes.push("Shock NONE: under 50 V.");
  } else {
    shock = expose ? "MEDIUM" : "LOW";
    causes.push(expose ? "Shock MEDIUM: Expose task crosses the limited approach boundary." : `Shock LOW: ${v} V, closed door. Shock protection on.`);
    if (near) shock = raiseTo(shock, "HIGH", "Shock HIGH: hands or tools inside the restricted approach boundary. Holds even with gloves.", "Inside the RAB");
    if (enclosure === "openOrDamaged") shock = raiseTo(shock, "MEDIUM", "Shock MEDIUM: open or damaged enclosure. Live parts may be reachable.", "Open or damaged");
    if (enclosure === "deadfrontMissing") shock = raiseTo(shock, "HIGH", "Shock HIGH: deadfront missing. Exposed to anyone.", "Deadfront missing");
  }

  // Arc baseline. The first row that matches wins.
  let arc: Level;
  if (v !== null && v < 50) {
    arc = "NONE";
    causes.push("Arc NONE: under 50 V.");
  } else if (sensed && !observe) {
    arc = "DANGER";
    causes.push(`Arc DANGER: ${sensesText} acknowledged. Evidence of impending failure, so arc flash is not unlikely. Table 130.5(C).`);
    follow("Work request");
  } else if (anyOverduty && t === "Operate-close") {
    arc = "DANGER";
    causes.push(`Arc DANGER: close or reset at or downstream of ${overdutyDevice}. A trip may mean a fault, and an overduty device may not clear it. NEC 110.9.`);
    follow("Engineering correction");
  } else if (anyOverduty && expose) {
    arc = "DANGER";
    causes.push(`Arc DANGER: Expose at or downstream of ${overdutyDevice}. It may not clear a fault at the point of work.`);
    follow("Engineering correction");
  } else if (overCeiling && !observe) {
    arc = "DANGER";
    causes.push(`Arc DANGER: IE ${ie} cal at or above the top stocked PPE (${input.topStockedPpeCal} cal).`);
    follow("Engineering review");
  } else if (noEewp && !observe) {
    arc = "DANGER";
    causes.push("Arc DANGER: IE above 40 cal, no approved EEWP.");
    follow("Engineering review");
  } else if (svc === "Nonserviceable" && !observe) {
    arc = "DANGER";
    causes.push("Arc DANGER: Nonserviceable. The device may not operate or clear a fault.");
    follow("Work request");
  } else if (pending && expose) {
    arc = "DANGER";
    causes.push("Arc DANGER: IE pending study. No energized work until IE is on record.");
    follow("Engineering review");
  } else if (observe) {
    arc = "LOW";
    causes.push(`Arc LOW: Observe task, no interaction.${anyOverduty ? " Overduty on record: inform only." : ""}${pending ? " IE pending study." : ""}`);
  } else if (pending && !(operate && svc === "Serviceable")) {
    arc = "HIGH";
    causes.push("Arc HIGH: IE pending study. Top stocked PPE, stand to the side.");
    follow("Engineering review");
  } else if (withEewp) {
    arc = "HIGH";
    causes.push(`Arc HIGH: IE ${ie} cal, approved EEWP on record.`);
  } else if (svc === "Limited Service") {
    arc = "HIGH";
    causes.push("Arc HIGH: Limited Service. Clearing time in doubt, IE may be understated.");
  } else if (operate && svc === "Serviceable") {
    arc = "LOW";
    causes.push(`Arc LOW: Serviceable, closed door. Table 130.5(C): unlikely.${pending ? " IE pending study: still on the study list." : ""}`);
  } else if (outsideStudy) {
    arc = "LOW";
    causes.push("Arc LOW: outside the arc flash study by design.");
  } else if (ie !== null && ie < 1.2) {
    arc = "LOW";
    causes.push(`Arc LOW: IE ${ie} cal, below 1.2. No arc flash PPE needed.`);
  } else {
    arc = "MEDIUM";
    causes.push(`Arc MEDIUM: IE ${ie} cal. Group 1 PPE.`);
  }
  if (anyOverduty && t === "Operate-open") {
    arc = raiseTo(arc, "HIGH", `Arc HIGH: opening at or downstream of ${overdutyDevice}. PPE on, stand to the side.`, "Overduty");
  }

  // Field findings
  if (arc !== "NONE") {
    if (operate && enclosure !== "secured") arc = raiseTo(arc, "MEDIUM", "Arc MEDIUM: enclosure not secured. NOC lost on Operate.", "Enclosure");
    if (fa.dustMoistureOrDebris) {
      if (observe) causes.push("Dust, moisture, or debris: warning on Observe. Recorded for maintenance.");
      else arc = raiseTo(arc, "HIGH", "Arc HIGH: dust, moisture, or debris on or near live parts. Slow down.", "Dust, moisture, or debris");
    }
    if (fa.damageBurnMarksOrDiscoloration) {
      if (observe) causes.push("Burn marks: recorded, work request. No raise on Observe.");
      else arc = raiseTo(arc, "MEDIUM", "Arc MEDIUM: burn marks. Old damage on record.", "Burn marks");
    }
    if (sensed && observe) {
      arc = raiseTo(arc, "HIGH", `Arc HIGH: ${sensesText} acknowledged. Evidence of impending failure.`, "Senses");
      follow("Work request");
    }
  }
  // Decision 64. Closing is an interaction the worker causes. Tools left inside, loose or wrong connections,
  // missing barriers: the Field Assessment cannot see inside. AF-Y2.
  const firstOp = input.firstOperationAfterWork === true && operate;
  if (firstOp && arc !== "NONE") arc = raiseTo(arc, "MEDIUM", "Arc MEDIUM: first operation after work. Someone may have left a tool, a loose connection, or a missing barrier. Table 130.5(C)(3), first operation after maintenance.", "First operation after work");
  if (fa.tamperSticker === "broken") causes.push("Tamper sticker broken: no raise. IE basis less certain.");
  if (notWalked) causes.push("Upstream protection not verified: site not walked. Uses this asset’s own overduty flag only.");
  if (lineSide) causes.push("Line side exposed: no level change. Isolation point moves to the device that feeds this housing.");

  // QEW Raise. Counted. Never lowers. Does not carry forward.
  const r = input.qewRaise;
  if (r) {
    if (r.hazard !== "arc" && shock !== "NONE" && shock !== "PENDING" && LEVEL_RANK[r.level] > LEVEL_RANK[shock]) {
      shock = r.level;
      causes.push(`Shock ${r.level}: raised by the QEW (${r.reason}). Counted, does not carry forward.`);
    }
    if (r.hazard !== "shock" && arc !== "NONE" && LEVEL_RANK[r.level] > LEVEL_RANK[arc]) {
      arc = r.level;
      causes.push(`Arc ${r.level}: raised by the QEW (${r.reason}). Counted, does not carry forward.`);
    }
  }

  // Action lines (RISK_ACTION_LINES.md)
  if (sensed) {
    if (observe && arc !== "NONE") line("Elevated risk acknowledged. Keep your distance. Don’t open or operate this equipment.");
    else if (!observe && arc !== "NONE") line("Elevated risk acknowledged. Don’t operate or open this equipment energized. De-energize upstream of it.");
    line("Submit a work request. Repair as required.");
  }
  if (anyOverduty && arc !== "NONE") {
    if (t === "Operate-open") line("Overduty: PPE on. Stand to the side. One motion.");
    else if (t === "Operate-close") line(`Don’t reset onto a possible fault. Find the cause. Restore from upstream of ${overdutyDevice}.`);
    else if (expose) line(`No energized work. Isolate upstream of ${overdutyDevice}. LOTO, verify, LDL.`);
    else line("Overduty on record. Inform only.");
  }
  if (!observe && overCeiling) line("No PPE covers this IE. De-energize. LOTO, verify, LDL.");
  if (!observe && noEewp) line("IE above 40 cal. Energized work needs an approved EEWP. De-energize.");
  if (!observe && svc === "Nonserviceable") line("Device may not clear a fault. De-energize. LOTO, verify, LDL.");
  if (!observe && svc === "Limited Service") line("IE may be understated, clearing time in doubt. De-energize if you can. If not, full controls.");
  if (pending) {
    if (expose) line(`${UNKNOWN_IE} No energized work.`, "unknown");
    else if (operate && svc !== "Serviceable") line(`${UNKNOWN_IE} Top PPE on. Stand to the side.`, "unknown");
    else line(UNKNOWN_IE, "unknown");
  }
  if (enclosure === "openOrDamaged") {
    line("Live parts may be reachable. Shock protection on. Work request.");
    follow("Work request");
  }
  if (enclosure === "deadfrontMissing") {
    line("Exposed to anyone. Barricade. Keep others out. Work request.");
    follow("Work request");
  }
  if (fa.dustMoistureOrDebris) line(observe ? "Dust, moisture, or debris on live parts. Recorded for maintenance." : "Conditions can arc over. Slow down. Full controls. Recorded for maintenance.");
  if (fa.damageBurnMarksOrDiscoloration) {
    line(observe ? "Burn marks recorded. Work request." : "Old damage on record. Arc PPE on. Work request.");
    follow("Work request");
  }
  if (firstOp && arc !== "NONE") line("First operation after work. Arc-rated PPE. Stand to the side. Turn your face away.");
  if (near) line("Gloves on before you reach in.");
  if (notWalked) line("Upstream protection not verified. Site not walked yet.");
  if (r) line(`Raised by you: ${r.reason}.`, "raisedByYou");

  let top: Level = LEVEL_RANK[shock] > LEVEL_RANK[arc] ? shock : arc;
  if (shock === "PENDING") top = "PENDING";
  if (arc === "DANGER" || shock === "DANGER") top = "DANGER";

  // Isolation point: the kind of point only, never a device name (decisions 55, 56).
  let isolationPoint: IsolationPoint;
  if (sensed) isolationPoint = { kind: "Upstream of this equipment", note: `Don’t operate the failing equipment’s own switch. ${LOCK_LINE}` };
  else if (anyOverduty && !input.overdutyThisDevice) isolationPoint = { kind: "Upstream of the overduty device", note: `The overduty device may not clear a fault. ${LOCK_LINE}` };
  else if (lineSide) isolationPoint = { kind: "The device that feeds this housing", note: `Line-side conductors stay live. ${LOCK_LINE}` };
  else isolationPoint = { kind: "Every source that feeds this equipment", note: LOCK_LINE };

  const riskButton = top === "PENDING" ? "Voltage Not On Record. Acknowledge And De-energize" : top === "DANGER" ? "Acknowledge And De-energize" : "Acknowledge The Risk";
  const forcedDeEnergize = top === "DANGER" || top === "PENDING";
  const deEnergized = input.tier === 3 || input.needPower === "deEnergize" || forcedDeEnergize;

  return {
    shockLevel: shock,
    arcLevel: arc,
    topLevel: top,
    headline: LEVEL_HEADLINE[top],
    actionLines,
    causes,
    followUp,
    isolationPoint,
    riskButton,
    forcedDeEnergize,
    deEnergized,
    rail: railOf(input.tier, deEnergized),
  };
}

/** The JSP rail. Field Assessment sits where Check Gear was (decision 52). */
export function railOf(tier: Tier, deEnergized: boolean): string[] {
  const rail = ["Job", "Fit For Duty"];
  if (tier === 2) rail.push("Need Power?");
  rail.push("Field Assessment", "Risk");
  if (deEnergized) return rail.concat(["Energy Sources", "PPE And Boundary", "Lock Out", "Test Dead", "Sign Off"]);
  rail.push("PPE And Boundary");
  if (tier === 2) rail.push("Do The Work");
  rail.push("Sign Off");
  return rail;
}

export interface LdlStep {
  phase: "verify" | "walk";
  label: string;
}

/**
 * The LDL rehearsal sequence (decision 57). Phase comes from the record's voltage:
 * 200 V and up, or blank, is three-phase (8 steps). Under 200 V is single-phase, line to neutral (5 steps).
 */
export function ldlSequence(voltage: number | null): LdlStep[] {
  const check: LdlStep = { phase: "verify", label: "Meter check, known live source" };
  const recheck: LdlStep = { phase: "verify", label: "Meter re-check, known live source" };
  const pairs = isThreePhase(voltage)
    ? ["L1 to Ground", "L2 to Ground", "L3 to Ground", "L1 to L2", "L1 to L3", "L2 to L3"]
    : ["Line to Neutral", "Line to Ground", "Neutral to Ground"];
  return [check, ...pairs.map((label): LdlStep => ({ phase: "walk", label })), recheck];
}

export function isThreePhase(voltage: number | null): boolean {
  return voltage === null || voltage >= 200;
}
